import { memo } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon, MapPinIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { useImageErrorHandler } from '../../hooks/useImageErrorHandler'
import ImageWithFallback from '../ImageWithFallback'

/**
 * EntityImageViewer - Full-screen image viewer with metadata
 * 
 * @param {Object} selectedImage - Current image being viewed
 * @param {number} selectedImageIndex - Index of current image in gallery
 * @param {Array} entityGallery - All gallery images
 * @param {Function} onClose - Handler to close viewer
 * @param {Function} onPrevious - Handler for previous image
 * @param {Function} onNext - Handler for next image
 */
const EntityImageViewer = memo(function EntityImageViewer({
  selectedImage,
  selectedImageIndex,
  entityGallery,
  onClose,
  onPrevious,
  onNext
}) {
  const { t } = useTranslation()
  const handleAvatarError = useImageErrorHandler()
  
  return (
    <div className="mb-2">
      <div className="relative rounded-lg bg-primary/5 border border-primary/20 overflow-hidden">
        {/* Top controls bar */}
        <div className="flex items-center justify-between px-2 pt-2 pb-1">
          <div className="flex items-center gap-1">
            {entityGallery.length > 1 && (
              <button
                onClick={onPrevious}
                className="bg-white/70 hover:bg-white border border-primary/20 p-1 rounded-lg transition-colors"
                aria-label={t('gallery.previousImage')}
              >
                <ChevronLeftIcon className="w-4 h-4 text-primary" />
              </button>
            )}
            {entityGallery.length > 1 && (
              <button
                onClick={onNext}
                className="bg-white/70 hover:bg-white border border-primary/20 p-1 rounded-lg transition-colors"
                aria-label={t('gallery.nextImage')}
              >
                <ChevronRightIcon className="w-4 h-4 text-primary" />
              </button>
            )}
            {entityGallery.length > 1 && (
              <span className="text-xs text-gray-500 ml-1">{selectedImageIndex + 1} / {entityGallery.length}</span>
            )}
          </div>

          <button
            onClick={onClose}
            className="bg-white/70 hover:bg-white border border-primary/20 p-1 rounded-lg transition-colors"
            aria-label={t('gallery.closeViewer')}
          >
            <XMarkIcon className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        
        <div className="px-2 pb-1">
          <ImageWithFallback
            src={selectedImage.imageUrl}
            alt={t('gallery.communityPhoto')}
            className="w-full max-h-[45vh] object-contain rounded-lg"
            showIconFallback={false}
          />
        </div>
        
        {/* Image metadata */}
        <div className="px-2 pb-2 space-y-1.5">
          {selectedImage.isPrimary && (
            <div className="flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-2 py-1">
              <StarIconSolid className="w-3.5 h-3.5" />
              {t('gallery.primaryImage', 'Primary Image')}
            </div>
          )}
          
          {selectedImage.user && (
            <div className="flex items-center gap-2 bg-white/50 rounded-lg px-2 py-1">
              <div className="flex-shrink-0">
                {selectedImage.user.avatar ? (
                  <img
                    src={selectedImage.user.avatar}
                    alt={`${selectedImage.user.firstName} ${selectedImage.user.lastName}`}
                    className="w-7 h-7 rounded-full object-cover border border-primary/20"
                    onError={handleAvatarError}
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <span className="text-primary font-semibold text-xs">
                      {selectedImage.user.firstName?.[0]}{selectedImage.user.lastName?.[0]}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 leading-tight">
                  {selectedImage.user.firstName} {selectedImage.user.lastName}
                </p>
                {selectedImage.createdAt && (
                  <p className="text-xs text-gray-500">
                    {new Date(selectedImage.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
            </div>
          )}
          
          {selectedImage.notes && (
            <div className="bg-white/50 rounded-lg px-2 py-1.5 border border-primary/10">
              <p className="text-xs font-semibold text-gray-600 mb-0.5">
                {t('gallery.sightingNotes', 'Sighting Notes')}
              </p>
              <p className="text-xs text-gray-700 leading-snug">
                {selectedImage.notes}
              </p>
            </div>
          )}
          
          {selectedImage.location?.address && (
            <div className="flex items-start gap-1.5 text-xs text-gray-600 bg-white/50 rounded-lg px-2 py-1">
              <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-primary" />
              <span className="line-clamp-2">{selectedImage.location.address}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export default EntityImageViewer
